import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Calendar, User, Tag, ArrowRight, Bookmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useBlogPost } from "@/hooks/useBlogPost";
import SharePromptButton from "@/components/prompts/SharePromptButton";

const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { post, isLoading, relatedPosts } = useBlogPost(slug || "");
  
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 pt-24 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-4xl">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-muted rounded w-3/4"></div>
              <div className="flex gap-4">
                <div className="h-4 bg-muted rounded w-20"></div>
                <div className="h-4 bg-muted rounded w-32"></div>
              </div>
              <div className="h-64 bg-muted rounded w-full"></div>
              <div className="space-y-3">
                <div className="h-4 bg-muted rounded w-full"></div>
                <div className="h-4 bg-muted rounded w-full"></div>
                <div className="h-4 bg-muted rounded w-3/4"></div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  
  if (!post) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 pt-24 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center py-16">
              <h1 className="text-2xl font-bold mb-4">Blog post not found</h1>
              <p className="text-muted-foreground mb-6">The blog post you're looking for doesn't exist or has been removed.</p>
              <Button asChild>
                <Link to="/blog">Back to Blog</Link>
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1 pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-4xl">
          {/* Back link */}
          <Link 
            to="/blog" 
            className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground mb-8"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to Blog
          </Link>
          
          {/* Header */}
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Badge>{post.category}</Badge>
              <span className="text-sm text-muted-foreground">
                <Clock className="inline-block w-4 h-4 mr-1" />
                {post.readTime} min read
              </span>
              <span className="text-sm text-muted-foreground">
                <Calendar className="inline-block w-4 h-4 mr-1" />
                {formatDate(post.publishedAt)}
              </span>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-extrabold mb-4">{post.title}</h1>
            <p className="text-xl text-muted-foreground mb-6">{post.excerpt}</p>
            
            <div className="flex items-center gap-3">
              <Avatar>
                <AvatarFallback>{post.author[0]}</AvatarFallback>
                {post.authorImage && <AvatarImage src={post.authorImage} />}
              </Avatar>
              <div>
                <div className="font-medium">{post.author}</div>
                {post.authorTitle && (
                  <div className="text-sm text-muted-foreground">{post.authorTitle}</div>
                )}
              </div>
            </div>
          </header>
          
          {/* Cover Image */}
          {post.coverImage && (
            <div className="mb-10 rounded-lg overflow-hidden">
              <img 
                src={post.coverImage} 
                alt={post.title} 
                className="w-full"
              />
            </div>
          )}
          
          {/* Content */}
          <div className="prose prose-lg dark:prose-invert max-w-none mb-10">
            <div dangerouslySetInnerHTML={{ __html: post.content }} />
          </div>
          
          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mb-10">
              <h3 className="text-lg font-bold mb-3">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {post.tags.map(tag => (
                  <Badge key={tag} variant="outline">{tag}</Badge>
                ))}
              </div>
            </div>
          )}
          
          {/* Share and Save */}
          <div className="flex justify-between items-center mb-10">
            <SharePromptButton
              promptId={slug || ""}
              title={post.title}
              description={post.excerpt}
              variant="outline"
              size="sm"
            />
            
            <Button variant="outline" size="sm" className="flex items-center gap-2">
              <Bookmark size={16} />
              Save
            </Button>
          </div>
          
          <Separator className="my-10" />
          
          {/* Related Posts */}
          {relatedPosts && relatedPosts.length > 0 && (
            <div className="mb-10">
              <h2 className="text-2xl font-bold mb-6">Related Posts</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {relatedPosts.map(relatedPost => (
                  <Link 
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.slug}`}
                    className="group flex flex-col bg-card border border-border/50 rounded-lg overflow-hidden hover:border-border transition-all"
                  >
                    {relatedPost.coverImage && (
                      <div className="aspect-video overflow-hidden">
                        <img 
                          src={relatedPost.coverImage} 
                          alt={relatedPost.title} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="text-sm text-muted-foreground mb-2">
                        {formatDate(relatedPost.publishedAt)}
                      </div>
                      <h3 className="font-medium mb-2 group-hover:text-primary transition-colors">
                        {relatedPost.title}
                      </h3>
                      <div className="flex items-center text-sm">
                        <span>{relatedPost.author}</span>
                        <span className="mx-2">•</span>
                        <span>{relatedPost.readTime} min read</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              
              <div className="text-center mt-8">
                <Button 
                  asChild
                  variant="outline" 
                  className="flex items-center gap-2"
                >
                  <Link to="/blog">
                    View all blog posts
                    <ArrowRight size={16} />
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogPostPage;
